import React, { useEffect, useState, useRef, lazy, memo } from 'react';
// build-refresh: 2026-04-30-refrescos-hero
import { useParams, useNavigate } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { RestaurantHeader } from '../components/restaurant/RestaurantHeader';
import { MenuItemCard } from '../components/restaurant/MenuItemCard';
import { MenuParentTabs } from '../components/restaurant/MenuParentTabs';
import { ProductDetailModal } from '../components/restaurant/ProductDetailModal';
import { FloatingCartButton } from '../components/restaurant/FloatingCartButton';
import { useRestaurantBySlug, useMenuData, useMarketplaceData } from '../lib/hooks';
import { useLanguage } from '../lib/i18n';
import { useCart } from '../lib/cart';
import { EmptyState } from '../components/ui/EmptyState';
import { Store, Plus, ArrowDown } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuItem, CartItemOption, MenuCategory } from '../lib/types';
import { formatCurrency } from '../lib/utils';
import { useKitchenPause } from '../lib/kitchen-pause-context';
import { useOpenOrderCheck } from '../lib/open-order-check';
import { OpenOrderAlert } from '../components/restaurant/OpenOrderAlert';
import { useTranslatedMenu } from '../lib/useTranslatedMenu';
import { translateCategory } from '../lib/translate';
import { useRestaurantScheduleStatus } from '../lib/schedule';
import { RestaurantClosedBanner } from '../components/restaurant/RestaurantClosedBanner';
// ─── Category Mapping Config ────────────────────────────────────────────────
// Kabú-specific parent grouping
const KABU_PARENT_CATEGORIES = [{
  key: 'comida',
  labelKey: 'cat_food',
  icon: '🍽️',
  subcategories: ['despertares del valle', 'sabores del mediodía', 'ensaladas', 'platos con memoria', 'de la plancha al corazón']
}, {
  key: 'bebidas',
  labelKey: 'cat_drinks',
  icon: '🥤',
  subcategories: ['bebidas calientes a base de café', 'bebidas calientes', 'bebidas frías a base de café', 'bebidas frías', 'shakes y smoothies', 'funcionales', 'bubble tea', 'cervezas', 'vinos', 'cócteles', 'mócteles', 'alcohol']
}, {
  key: 'postres',
  labelKey: 'cat_desserts',
  icon: '🍰',
  subcategories: ['postres']
}, {
  key: 'ninos',
  labelKey: 'cat_kids',
  icon: '👶',
  subcategories: ['para los pequeños']
}];
function getKabuParentKey(categoryName: string): string {
  const nameLower = categoryName.toLowerCase().trim();
  for (const parent of KABU_PARENT_CATEGORIES) {
    if (parent.subcategories.includes(nameLower)) {
      return parent.key;
    }
  }
  return 'comida';
}
// Generic emoji mapping for any category name
function getCategoryIcon(name: string): string {
  const lower = name.toLowerCase().trim();
  if (lower.includes('tacita')) return 'https://cdn.magicpatterns.com/uploads/nvDy4MrojnMpunka4CSaPN/Screenshot_2026-04-26_at_11.46.35_PM.png';
  if (lower.includes('pizza')) return '🍕';
  if (lower.includes('hamburguesa') || lower.includes('burger')) return '🍔';
  if (lower.includes('ensalada') || lower.includes('salad')) return '🥗';
  if (lower.includes('bebida') || lower.includes('drink') || lower.includes('refresco') || lower.includes('milkshake')) return '🥤';
  if (lower.includes('postre') || lower.includes('dessert') || lower.includes('copa')) return '🍨';
  if (lower.includes('favorito') || lower.includes('popular')) return '🔥';
  if (lower.includes('antojo') || lower.includes('dulce')) return '🍫';
  if (lower.includes('refrescante') || lower.includes('granizado')) return '🍧';
  if (lower.includes('fresca') || lower.includes('saludable') || lower.includes('fruta')) return '🍓';
  if (lower.includes('salado') || lower.includes('diferente')) return 'https://cdn.magicpatterns.com/uploads/f6PdBQJuuvgFKAyM5AdiMp/Screenshot_2026-04-26_at_11.47.18_PM.png';
  if (lower.includes('combo') || lower.includes('promo')) return '🎉';
  if (lower.includes('entrada') || lower.includes('appetizer')) return '🍽️';
  if (lower.includes('taco')) return '🌮';
  if (lower.includes('sopa') || lower.includes('soup')) return '🍲';
  if (lower.includes('pasta')) return '🍝';
  if (lower.includes('sandwich') || lower.includes('sándwich')) return '🥪';
  if (lower.includes('desayuno') || lower.includes('breakfast')) return '🍳';
  if (lower.includes('niño') || lower.includes('kid') || lower.includes('pequeño')) return '👶';
  if (lower.includes('extra') || lower.includes('adicional')) return '✨';
  if (lower.includes('especial') || lower.includes('encargo')) return 'https://cdn.magicpatterns.com/uploads/p9c7FqKqxJzUz1k1HX2mEK/Screenshot_2026-04-26_at_11.48.15_PM.png';
  if (lower.includes('café') || lower.includes('coffee')) return '☕';
  if (lower.includes('cerveza') || lower.includes('beer')) return '🍺';
  if (lower.includes('vino') || lower.includes('wine')) return '🍷';
  if (lower.includes('cóctel') || lower.includes('cocktail')) return '🍹';
  return '🍽️';
}
// Priority sort: pizza-related categories first, then by DB sort_order
function getCategorySortPriority(name: string): number {
  const lower = name.toLowerCase().trim();
  if (lower.includes('pizza')) return 0;
  if (lower.includes('hamburguesa') || lower.includes('burger')) return 1;
  if (lower.includes('combo')) return 2;
  if (lower.includes('especial')) return 3;
  if (lower.includes('entrada')) return 4;
  if (lower.includes('ensalada')) return 5;
  if (lower.includes('postre')) return 80;
  if (lower.includes('bebida') || lower.includes('refresco')) return 90;
  if (lower.includes('extra') || lower.includes('adicional')) return 95;
  return 50;
}
// ─── Congelados category order ─────────────────────────────────────────
const CONGELADOS_CATEGORY_ORDER = ['favoritos', 'bebidas frías & milkshakes', 'copas & postres helados', 'antojos dulces', 'refrescantes & granizados', 'opciones frescas', 'salados', 'combos & promos'];
const getCongeladosCategoryRank = (name?: string | null): number => {
  const normalized = String(name ?? '').trim().toLowerCase();
  const idx = CONGELADOS_CATEGORY_ORDER.findIndex(item => normalized === item);
  return idx === -1 ? 999 : idx;
};
// ─── NatuCafé category order ───────────────────────────────────────────
// NatuCafé: category order is driven by DB sort_order (no hardcoded override)
const getNatuCafeCategoryRank = (cat?: {
  name?: string | null;
  sort_order?: number;
} | string | null): number => {
  if (typeof cat === 'string' || cat === null || cat === undefined) {
    // Legacy call with just name — return high number so sort_order wins
    return 999;
  }
  return cat.sort_order ?? 999;
};
// ─── NatuCafé Parent Category Grouping ──────────────────────────────────
const NATUCAFE_PARENT_CATEGORIES = [{
  key: 'bebidas',
  labelKey: 'Bebidas y Repostería',
  icon: '☕',
  subcategories: ['bebidas calientes', 'bebidas frías', 'té y más', 'aguadulce', 'batidos', 'batidos en agua', 'batidos con leche', 'postres', 'repostería', 'reposteria']
}, {
  key: 'comida',
  labelKey: 'Comida',
  icon: '🍽️',
  subcategories: [] // catch-all for everything else
}];
function getNatuCafeParentKey(categoryName: string): string {
  const nameLower = categoryName.toLowerCase().trim();
  const bebidasGroup = NATUCAFE_PARENT_CATEGORIES[0];
  if (bebidasGroup.subcategories.some(sub => nameLower.includes(sub) || sub.includes(nameLower))) {
    return 'bebidas';
  }
  return 'comida';
}
export function RestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    t,
    lang
  } = useLanguage();
  const {
    restaurant,
    settings,
    loading: restaurantLoading,
    error
  } = useRestaurantBySlug(slug);
  const {
    categories,
    items,
    loading: menuLoading
  } = useMenuData(restaurant?.id);
  const {
    translatedItems,
    translatedCategories
  } = useTranslatedMenu(items, categories, lang);
  const {
    zones
  } = useMarketplaceData();
  const {
    addItem
  } = useCart();
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  // Schedule-based open/closed enforcement (re-evaluates every 30s)
  const scheduleStatus = useRestaurantScheduleStatus(restaurant?.slug);
  const isClosedBySchedule = scheduleStatus.isClosedForOrdering;
  const {
    openOrder,
    shouldAlert,
    dismiss: dismissOpenOrder
  } = useOpenOrderCheck(restaurant?.id);
  const [showOpenOrderAlert, setShowOpenOrderAlert] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<{
    item: MenuItem;
    quantity: number;
    options: CartItemOption[];
    notes: string;
  } | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeParent, setActiveParent] = useState<string>(slug?.includes('natucafe') ? 'bebidas' : 'comida');
  const [activeSubcategory, setActiveSubcategory] = useState<string>('');
  const [showEncargoPopup, setShowEncargoPopup] = useState(false);
  const [pendingEncargoKey, setPendingEncargoKey] = useState<string | null>(null);
  const menuSectionRef = useRef<HTMLDivElement>(null);
  // ─── Slug flags (MUST be before any code that references them) ─────────
  const isCongeladosSlug = slug === 'heladeria-congelados';
  const isNatuCafeSlug = slug?.includes('natucafe') ?? false;
  useEffect(() => {
    if (restaurant?.id) {
      monitorRestaurant(restaurant.id);
    }
  }, [restaurant?.id, monitorRestaurant]);
  // ─── Map Categories to Parents ─────────────────────────────────────────
  const isCrackPollo = restaurant?.slug === 'crack-pollo' || String(restaurant?.id) === '20';
  const categoriesWithItems = (categories ?? []).filter(Boolean).filter(cat => (items ?? []).some(item => item?.category_id === cat.id)).filter(cat => !(isCrackPollo && cat.name?.toLowerCase().trim() === 'otros'));
  // Determine if this restaurant uses parent grouping
  const useKabuGrouping = restaurant?.slug === 'kabu';
  const useNatuCafeGrouping = isNatuCafeSlug;
  const parentGroups = useKabuGrouping ?
  // Kabú: use the hardcoded parent category system
  KABU_PARENT_CATEGORIES.map(parent => {
    const subs = categoriesWithItems.filter(cat => getKabuParentKey(cat.name) === parent.key);
    const itemCount = (items ?? []).filter(item => subs.some(sub => sub.id === item?.category_id)).length;
    return {
      ...parent,
      subcategories: subs,
      itemCount
    };
  }).filter(p => p.itemCount > 0) : useNatuCafeGrouping ?
  // NatuCafé: two parent groups (Bebidas y Postres / Comida)
  NATUCAFE_PARENT_CATEGORIES.map(parent => {
    const subs = categoriesWithItems.filter(cat => getNatuCafeParentKey(cat.name) === parent.key).sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0));
    const itemCount = (items ?? []).filter(item => subs.some(sub => sub.id === item?.category_id)).length;
    return {
      ...parent,
      subcategories: subs,
      itemCount
    };
  }).filter(p => p.itemCount > 0) :
  // Generic: check if DB has parent categories
  (() => {
    const allCats = categories ?? [];
    // Find parents: explicitly marked OR referenced as parent by children
    const referencedParentIds = new Set(allCats.filter(c => c.parent_category_id && String(c.parent_category_id) !== String(c.id)).map(c => String(c.parent_category_id)));
    // Also include self-referencing categories as parents (e.g. id 181 with parent_category_id=181)
    const selfRefIds = new Set(allCats.filter(c => c.parent_category_id && String(c.parent_category_id) === String(c.id)).map(c => String(c.id)));
    const dbParents = allCats.filter(c => c.is_active !== false && (c.is_parent === true || referencedParentIds.has(String(c.id)) || selfRefIds.has(String(c.id))));
    // Deduplicate: a self-referencing cat might appear as both parent and child
    const parentIdSet = new Set(dbParents.map(p => String(p.id)));
    if (dbParents.length > 0) {
      // DB-driven parent grouping
      const groups = dbParents.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map(parent => {
        // Child subcategories assigned to this parent
        const childSubs = categoriesWithItems.filter(c => String(c.parent_category_id) === String(parent.id) && String(c.id) !== String(parent.id));
        // If the parent itself has items directly assigned, include it as a sub too
        const parentHasItems = categoriesWithItems.some(c => String(c.id) === String(parent.id));
        const subs = parentHasItems ? [...categoriesWithItems.filter(c => String(c.id) === String(parent.id)), ...childSubs] : childSubs;
        const itemCount = (items ?? []).filter(item => subs.some(sub => sub.id === item?.category_id)).length;
        return {
          key: String(parent.id),
          labelKey: parent.name ?? '',
          icon: getCategoryIcon(parent.name ?? ''),
          subcategories: subs.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
          itemCount
        };
      }).filter(p => p.itemCount > 0);
      // NatuCafé override: move "Té y más" from Comida to Bebidas y Postres (after Bebidas frías)
      if (isNatuCafeSlug) {
        const bebidasGroup = groups.find(g => g.labelKey.toLowerCase().includes('bebidas'));
        const comidaGroup = groups.find(g => g.labelKey.toLowerCase().includes('comida'));
        if (bebidasGroup && comidaGroup) {
          const teymasIdx = comidaGroup.subcategories.findIndex(c => c.name?.toLowerCase().includes('té y más'));
          if (teymasIdx !== -1) {
            const [teymas] = comidaGroup.subcategories.splice(teymasIdx, 1);
            // Insert after "Bebidas frías"
            const friasIdx = bebidasGroup.subcategories.findIndex(c => c.name?.toLowerCase().includes('bebidas frías'));
            bebidasGroup.subcategories.splice(friasIdx !== -1 ? friasIdx + 1 : bebidasGroup.subcategories.length, 0, teymas);
            // Recalculate item counts
            comidaGroup.itemCount = (items ?? []).filter(item => comidaGroup.subcategories.some(sub => sub.id === item?.category_id)).length;
            bebidasGroup.itemCount = (items ?? []).filter(item => bebidasGroup.subcategories.some(sub => sub.id === item?.category_id)).length;
          }
        }
      }
      // Orphan categories: have items, not a parent, no parent_category_id.
      // Each orphan becomes its own top-level group using its real DB name —
      // never grouped under a fake "Más" parent.
      const orphans = categoriesWithItems.filter(c => !parentIdSet.has(String(c.id)) && !c.parent_category_id && !c.is_parent);
      orphans.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).forEach(cat => {
        const itemCount = (items ?? []).filter(item => item?.category_id === cat.id).length;
        if (itemCount > 0) {
          groups.push({
            key: String(cat.id),
            labelKey: cat.name ?? '',
            icon: getCategoryIcon(cat.name ?? ''),
            subcategories: [cat],
            itemCount
          });
        }
      });
      return groups;
    }
    // Flat fallback: each DB category becomes its own "parent" tab
    return [...categoriesWithItems].sort((a, b) => {
      if (isCongeladosSlug) {
        const rankDiff = getCongeladosCategoryRank(a?.name) - getCongeladosCategoryRank(b?.name);
        if (rankDiff !== 0) return rankDiff;
        return (a?.sort_order ?? 0) - (b?.sort_order ?? 0);
      }
      // Admin-managed sort_order WINS — the hardcoded name-based
      // priority only acts as a tiebreaker for legacy data where
      // sort_order is missing/equal. This is what makes the
      // "Ordenar Categorías" drawer actually take effect on the
      // public menu (Emilius Pizza, etc.).
      const orderA = a?.sort_order ?? 0;
      const orderB = b?.sort_order ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      return getCategorySortPriority(a?.name ?? '') - getCategorySortPriority(b?.name ?? '');
    }).map(cat => ({
      key: cat.id,
      labelKey: cat.name ?? '',
      icon: getCategoryIcon(cat.name ?? ''),
      subcategories: [cat],
      itemCount: (items ?? []).filter(item => item?.category_id === cat.id).length
    })).filter(p => p.itemCount > 0);
  })();
  useEffect(() => {
    if (parentGroups.length > 0 && !parentGroups.find(p => p.key === activeParent)) {
      const firstParent = parentGroups[0];
      setActiveParent(firstParent.key);
      if (firstParent.subcategories.length > 0) {
        setActiveSubcategory(firstParent.subcategories[0].id);
      }
    } else if (parentGroups.length > 0) {
      const currentParent = parentGroups.find(p => p.key === activeParent);
      if (currentParent && currentParent.subcategories.length > 0 && !activeSubcategory) {
        setActiveSubcategory(currentParent.subcategories[0].id);
      }
    }
  }, [parentGroups, activeParent, activeSubcategory]);
  const handleProductClick = (item: MenuItem) => {
    setSelectedProduct(item);
    setIsModalOpen(true);
  };
  const handleAddToCart = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    if (!restaurant) return;
    if (isKitchenPaused) {
      alert('La cocina está pausada en este momento. Vuelve en 10 minutos.');
      return;
    }
    if (isClosedBySchedule) {
      alert(`${restaurant.name} está cerrado en este momento. ${scheduleStatus.nextOpeningHint || 'Vuelve durante nuestro horario de atención.'}`);
      return;
    }
    // Show open order alert on first add attempt
    if (shouldAlert) {
      console.log('[OpenOrderCheck] Intercepting add-to-cart — showing alert');
      setPendingAdd({
        item,
        quantity,
        options,
        notes
      });
      setShowOpenOrderAlert(true);
      return;
    }
    const success = addItem({
      menu_item_id: item.id,
      restaurant_id: restaurant.id,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options,
      station_code: item.station_code || null
    }, restaurant.name, restaurant.slug);
    if (!success) {
      alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
    }
  };
  const handleDismissOpenOrder = () => {
    dismissOpenOrder();
    setShowOpenOrderAlert(false);
    // Process the pending add
    if (pendingAdd && restaurant) {
      if (isClosedBySchedule) {
        alert(`${restaurant.name} está cerrado en este momento. ${scheduleStatus.nextOpeningHint || 'Vuelve durante nuestro horario de atención.'}`);
        setPendingAdd(null);
        return;
      }
      const {
        item,
        quantity,
        options,
        notes
      } = pendingAdd;
      const success = addItem({
        menu_item_id: item.id,
        restaurant_id: restaurant.id,
        item_name_snapshot: item.name,
        unit_price_snapshot: item.price,
        quantity,
        notes,
        image_url: item.image_url,
        selected_options: options,
        station_code: item.station_code || null
      }, restaurant.name, restaurant.slug);
      if (!success) {
        alert('Ya tienes productos de otro restaurante en tu carrito. Por favor, finaliza o vacía tu pedido actual primero.');
      }
      setPendingAdd(null);
    }
  };
  const handleCancelOpenOrder = () => {
    setShowOpenOrderAlert(false);
    setPendingAdd(null);
  };
  const handleSelectParent = (parentKey: string) => {
    // Heladería Congelados: intercept "Especialidades por Encargo"
    if (isCongeladosSlug) {
      const parent = parentGroups.find(p => p.key === parentKey);
      if (parent?.labelKey?.toLowerCase().includes('encargo') || parent?.subcategories?.[0]?.name?.toLowerCase().includes('encargo')) {
        setPendingEncargoKey(parentKey);
        setShowEncargoPopup(true);
        return;
      }
    }
    setActiveParent(parentKey);
    const parent = parentGroups.find(p => p.key === parentKey);
    if (parent && parent.subcategories.length > 0) {
      setActiveSubcategory(parent.subcategories[0].id);
    }
    scrollToMenu();
  };
  const handleSelectSubcategory = (categoryId: string) => {
    // Heladería Congelados: intercept "Especialidades por Encargo"
    if (isCongeladosSlug) {
      const allSubs = parentGroups.flatMap(p => p.subcategories);
      const cat = allSubs.find(c => c.id === categoryId);
      if (cat?.name?.toLowerCase().includes('encargo')) {
        setShowEncargoPopup(true);
        return;
      }
    }
    setActiveSubcategory(categoryId);
  };
  const scrollToMenu = () => {
    if (menuSectionRef.current) {
      const y = menuSectionRef.current.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  // ─── Loading ───
  if (restaurantLoading) {
    return <div className={`min-h-screen w-full flex flex-col ${isNatuCafeSlug ? 'bg-[#0F0D0B]' : isCongeladosSlug ? 'bg-[#FBF5FF]' : 'bg-[#FBF7F2]'}`} data-id="element-6424">
        <MarketplaceHeader data-id="element-6425" />
        <div className={`h-48 sm:h-64 w-full ${isNatuCafeSlug ? 'bg-[#1A1614]' : isCongeladosSlug ? 'bg-[#E8DDF0]' : 'bg-[#F5EDE3]'} animate-pulse`} data-id="element-6426" />
        <div className="max-w-3xl mx-auto px-4 w-full mt-10 space-y-4" data-id="element-6427">
          <div className={`h-8 w-48 ${isNatuCafeSlug ? 'bg-[#2A2420]' : isCongeladosSlug ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'} rounded-lg animate-pulse`} data-id="element-6428" />
          <div className={`h-4 w-72 ${isNatuCafeSlug ? 'bg-[#2A2420]' : isCongeladosSlug ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'} rounded animate-pulse`} data-id="element-6429" />
          <div className="space-y-3 mt-8" data-id="element-6430">
            {[1, 2, 3, 4].map(i => <div key={i} className={`h-20 rounded-xl ${isNatuCafeSlug ? 'bg-[#2A2420]' : isCongeladosSlug ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'} animate-pulse`} data-id="element-6431" />)}
          </div>
        </div>
      </div>;
  }
  // ─── Error ───
  if (error || !restaurant) {
    return <div className={`min-h-screen w-full flex flex-col ${isNatuCafeSlug ? 'bg-[#0F0D0B]' : isCongeladosSlug ? 'bg-[#FBF5FF]' : 'bg-[#FBF7F2]'}`} data-id="element-6432">
        <MarketplaceHeader data-id="element-6433" />
        <div className="flex-1 flex items-center justify-center" data-id="element-6434">
          <EmptyState icon={Store} title="Restaurante no encontrado" description="El restaurante que buscas no existe o no está disponible en este momento." actionLabel="Volver al inicio" onAction={() => navigate('/')} data-id="element-6435" />
        </div>
        <Footer data-id="element-6436" />
      </div>;
  }
  const zoneName = zones.find(z => z.id === restaurant.zone_id)?.name;
  const isEmilius = restaurant.slug === 'emilius-pizza';
  const isKabu = restaurant.slug === 'kabu';
  const isCongelados = restaurant.slug === 'heladeria-congelados';
  const isNatuCafe = restaurant.slug?.includes('natucafe') ?? false;
  const isKyomu = restaurant.slug === 'kyomu-sushi';
  const isCabanaParrillera = String(restaurant.id) === '24' || restaurant.slug === 'cabana-parrillera' || restaurant.slug === 'la-cabana-parrillera';
  const primaryColor = isKyomu ? '#D88EA8' : isEmilius ? '#D42B2B' : isKabu ? '#D4A853' : isCongelados ? '#7B2D8E' : isNatuCafe ? '#C6A969' : isCabanaParrillera ? '#FF6B35' : restaurant.primary_color || '#8B5E3C';
  const activeParentData = parentGroups.find(p => p.key === activeParent);
  const bgColor = isKyomu ? 'bg-[#1A1215]' : isNatuCafe ? 'bg-[#0F0D0B]' : isCongelados ? 'bg-[#FBF5FF]' : isCabanaParrillera ? 'bg-[#0B0807]' : 'bg-[#FBF7F2]';
  const borderColor = isKyomu ? 'border-[#3A2030]' : isNatuCafe ? 'border-[#2A2420]' : isCongelados ? 'border-[#E8DDF0]' : isCabanaParrillera ? 'border-[#3A1F0F]' : 'border-[#E8DDD0]';
  const titleColor = isKyomu ? 'text-[#F5E8EE]' : isNatuCafe ? 'text-[#F5F0EB]' : isCongelados ? 'text-[#7B2D8E]' : isCabanaParrillera ? 'text-[#FFB347]' : 'text-[#3D2B1F]';
  const subtitleColor = isKyomu ? 'text-[#D88EA8]' : isNatuCafe ? 'text-[#C6A969]' : isCongelados ? 'text-[#5D3A1A]' : isCabanaParrillera ? 'text-[#E8C9A0]' : 'text-[#8B7355]';
  const mutedTextColor = isKyomu ? 'text-[#D88EA8]/70' : isNatuCafe ? 'text-[#8B7B6B]' : isCongelados ? 'text-[#7B2D8E]/70' : isCabanaParrillera ? 'text-[#A89682]' : 'text-[#8B7355]/70';
  const hoverBgColor = isKyomu ? 'hover:bg-[#D88EA8]/10' : isNatuCafe ? 'hover:bg-[#C6A969]/5' : isCongelados ? 'hover:bg-[#7B2D8E]/5' : isCabanaParrillera ? 'hover:bg-[#FF6B35]/10' : 'hover:bg-[#8B5E3C]/5';
  // Crack Pollo: curated featured items (always these two, in this order)
  const CRACKPOLLO_FEATURED: string[][] = [['los nacho pop', 'nacho pop', 'los nachopop', 'nachopop'], ['salchipop', 'salchi pop', 'los salchipop']];
  // NatuCafé: exclude items without photos and specific items
  const NATUCAFE_EXCLUDED = ['sánguche de queso', 'sanguche de queso', 'arroz con camarones', 'pollo a la plancha', 'spaguetti fradiablo', 'palomitas de pollo', 'spaguetti a la mantequilla'];
  const NATUCAFE_EXCLUDED_CATEGORIES = ['bebidas calientes', 'bebidas frías', 'aguadulce', 'batidos en agua', 'batidos con leche', 'batidos', 'postres'];
  const highlightedItems = isCrackPollo ? CRACKPOLLO_FEATURED.map(aliases => items.find(i => {
    const n = i.name?.toLowerCase().trim() ?? '';
    return aliases.some(a => n === a || n.includes(a));
  })).filter(Boolean) as MenuItem[] : isNatuCafe ? items.filter(i => i.image_url).filter(i => !NATUCAFE_EXCLUDED.includes(i.name?.toLowerCase().trim() ?? '')).filter(i => {
    const cat = categories.find(c => c.id === i.category_id);
    return !cat || !NATUCAFE_EXCLUDED_CATEGORIES.includes(cat.name?.toLowerCase().trim() ?? '');
  }).slice(0, 3) : items.slice(0, 3);
  return <div className={`min-h-screen w-full flex flex-col ${bgColor} pb-24`} data-id="element-6437">
      <MarketplaceHeader data-id="element-6438" />

      <main className="flex-1 relative" data-id="element-6439">
        <RestaurantHeader restaurant={restaurant} settings={settings} zoneName={zoneName} data-id="element-6440" />

        {isClosedBySchedule && <RestaurantClosedBanner nextOpeningHint={scheduleStatus.nextOpeningHint} variant={isNatuCafe || isCabanaParrillera ? 'dark' : 'light'} data-id="element-6441" />}

        {/* Decorative coffee plant elements - NatuCafé only */}
        {isNatuCafe && <>
            {/* Top right coffee branch */}
            <div className="absolute top-20 right-0 w-64 h-64 pointer-events-none opacity-[0.08] z-0" data-id="element-6442">
              <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6443">
                {/* Coffee branch with leaves and berries */}
                <path d="M20 180 Q 60 140, 80 100 T 120 40" stroke="#2A2420" strokeWidth="3" fill="none" data-id="element-6444" />
                {/* Leaves */}
                <ellipse cx="50" cy="160" rx="18" ry="28" fill="#2A2420" opacity="0.9" transform="rotate(-25 50 160)" data-id="element-6445" />
                <ellipse cx="70" cy="130" rx="20" ry="30" fill="#C6A969" opacity="0.3" transform="rotate(15 70 130)" data-id="element-6446" />
                <ellipse cx="90" cy="95" rx="18" ry="28" fill="#2A2420" opacity="0.9" transform="rotate(-30 90 95)" data-id="element-6447" />
                <ellipse cx="105" cy="70" rx="16" ry="24" fill="#2A2420" opacity="0.8" transform="rotate(20 105 70)" data-id="element-6448" />
                <ellipse cx="115" cy="50" rx="14" ry="22" fill="#C6A969" opacity="0.3" transform="rotate(-15 115 50)" data-id="element-6449" />
                {/* Coffee berries */}
                <circle cx="65" cy="145" r="5" fill="#D4883A" opacity="0.4" data-id="element-6450" />
                <circle cx="68" cy="148" r="4" fill="#D4883A" opacity="0.4" data-id="element-6451" />
                <circle cx="95" cy="110" r="5" fill="#D4883A" opacity="0.4" data-id="element-6452" />
                <circle cx="98" cy="107" r="4" fill="#D4883A" opacity="0.4" data-id="element-6453" />
              </svg>
            </div>

            {/* Bottom left coffee branch */}
            <div className="absolute bottom-32 left-0 w-56 h-56 pointer-events-none opacity-[0.06] z-0" data-id="element-6454">
              <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-6455">
                <path d="M180 20 Q 140 60, 120 100 T 80 160" stroke="#2A2420" strokeWidth="3" fill="none" data-id="element-6456" />
                <ellipse cx="150" cy="40" rx="16" ry="26" fill="#2A2420" opacity="0.9" transform="rotate(25 150 40)" data-id="element-6457" />
                <ellipse cx="130" cy="70" rx="18" ry="28" fill="#C6A969" opacity="0.3" transform="rotate(-15 130 70)" data-id="element-6458" />
                <ellipse cx="110" cy="105" rx="17" ry="26" fill="#2A2420" opacity="0.9" transform="rotate(30 110 105)" data-id="element-6459" />
                <ellipse cx="95" cy="130" rx="15" ry="23" fill="#2A2420" opacity="0.8" transform="rotate(-20 95 130)" data-id="element-6460" />
                <circle cx="135" cy="55" r="5" fill="#D4883A" opacity="0.4" data-id="element-6461" />
                <circle cx="115" cy="90" r="4" fill="#D4883A" opacity="0.4" data-id="element-6462" />
                <circle cx="100" cy="118" r="5" fill="#D4883A" opacity="0.4" data-id="element-6463" />
              </svg>
            </div>
          </>}

        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 relative z-10" data-id="element-6464">
          {/* ─── Highlighted Items — editorial feature cards ─── */}
          {highlightedItems.length > 0 && <section className="mb-10 sm:mb-14" data-id="element-6465">
              <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${mutedTextColor} mb-4`} data-id="element-6466">
                {t('recommended')}
              </p>
              <div className="space-y-1" data-id="element-6467">
                {highlightedItems.map(item => <div key={item.id} onClick={() => handleProductClick(item)} className={`group flex items-center gap-4 py-3.5 cursor-pointer border-b ${borderColor} last:border-b-0 ${hoverBgColor} transition-colors rounded-lg`} data-id="element-6468">
                    {Number(restaurant?.id) === 24 ? <div className="w-14 h-14 rounded-xl bg-white shrink-0" data-id="element-6469" /> : restaurant?.slug === 'crack-pollo' && !item.image_url ? <div className="w-14 h-14 rounded-xl bg-white shrink-0" data-id="element-6470" /> : <img src={item.image_url || 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=300&h=300&fit=crop&q=60'} alt={item.name} className="w-14 h-14 rounded-xl object-cover shrink-0" loading="lazy" decoding="async" data-id="element-6471" />}
                    <div className="flex-1 min-w-0" data-id="element-6472">
                      <h3 className={`font-semibold ${titleColor} text-[15px] leading-tight line-clamp-1 group-hover:opacity-80 transition-opacity`} data-id="element-6473">
                        {translatedItems.find(ti => ti.id === item.id)?.name || item.name}
                      </h3>
                      {item.description && <p className={`text-[12px] ${subtitleColor} line-clamp-1 mt-0.5`} data-id="element-6474">
                          {translatedItems.find(ti => ti.id === item.id)?.description || item.description}
                        </p>}
                    </div>
                    <span className="font-semibold text-sm shrink-0" style={{
                color: primaryColor
              }} data-id="element-6475">
                      {formatCurrency(item.price)}
                    </span>
                  </div>)}
              </div>
            </section>}

          {/* ─── Parent Category Selector — minimal inline pills ─── */}
          <section className="mb-10 sm:mb-14" data-id="element-6476">
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${mutedTextColor} mb-5`} data-id="element-6477">
              {t('explore')}
            </p>
            {menuLoading ? <div className="flex gap-3" data-id="element-6478">
                {[1, 2, 3, 4].map(i => <div key={i} className={`h-16 w-28 rounded-2xl ${isKyomu ? 'bg-[#3A2030]' : isNatuCafe ? 'bg-[#2A2420]' : isCongelados ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'} animate-pulse`} data-id="element-6479" />)}
              </div> : parentGroups.length > 0 ? <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-1" data-id="element-6480">
                {parentGroups.map(parent => {
              const isActive = activeParent === parent.key;
              return <button key={parent.key} onClick={() => handleSelectParent(parent.key)} className="shrink-0 flex flex-col items-center gap-2 px-6 py-4 rounded-2xl transition-all active:scale-95" style={{
                backgroundColor: isActive ? `${primaryColor}15` : isKyomu ? '#1A1215' : isNatuCafe ? '#1A1614' : '#FFFFFF',
                borderWidth: 1,
                borderColor: isActive ? `${primaryColor}40` : isKyomu ? '#3A2030' : isNatuCafe ? '#2A2420' : isCongelados ? '#E8DDF0' : '#E8DDD0'
              }} data-id="element-6481">
                      {parent.icon.startsWith('http') ? <img src={parent.icon} alt="" className="w-8 h-8 object-cover rounded-full" loading="lazy" decoding="async" data-id="element-6482" /> : <span className="text-2xl" data-id="element-6483">{parent.icon}</span>}
                      <span className={`text-xs font-semibold ${isActive ? titleColor : mutedTextColor}`} data-id="element-6484">
                        {t(parent.labelKey)}
                      </span>
                    </button>;
            })}
              </div> : <p className={`${mutedTextColor} text-sm`} data-id="element-6485">
                No hay categorías disponibles.
              </p>}
          </section>

          {/* ─── Subtle divider with scroll hint ─── */}
          <div className="flex items-center gap-3 mb-8" data-id="element-6486">
            <div className={`flex-1 h-px ${isKyomu ? 'bg-[#3A2030]' : isNatuCafe ? 'bg-[#2A2420]' : isCongelados ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'}`} data-id="element-6487" />
            <button onClick={scrollToMenu} className={`flex items-center gap-1.5 ${isKyomu ? 'text-[#D88EA8]/50 hover:text-[#D88EA8]' : isNatuCafe ? 'text-[#C6A969]/50 hover:text-[#C6A969]' : isCongelados ? 'text-[#7B2D8E]/50 hover:text-[#7B2D8E]' : 'text-[#8B7355]/50 hover:text-[#8B7355]'} transition-colors`} data-id="element-6488">
              <span className="text-[11px] font-medium uppercase tracking-widest" data-id="element-6489">
                {t('menu')}
              </span>
              <ArrowDown className="w-3 h-3" data-id="element-6490" />
            </button>
            <div className={`flex-1 h-px ${isKyomu ? 'bg-[#3A2030]' : isNatuCafe ? 'bg-[#2A2420]' : isCongelados ? 'bg-[#E8DDF0]' : 'bg-[#E8DDD0]'}`} data-id="element-6491" />
          </div>

          {/* ═══ FULL MENU SECTION ═══ */}
          <div ref={menuSectionRef} data-id="element-6492">
            {parentGroups.length > 0 && activeParentData && <>
                <MenuParentTabs parents={parentGroups.map(p => ({
              key: p.key,
              label: t(p.labelKey),
              icon: p.icon
            }))} activeParent={activeParent} onSelectParent={handleSelectParent} subcategories={activeParentData.subcategories.map(c => ({
              ...c,
              name: translateCategory(c.name, lang)
            }))} activeSubcategory={activeSubcategory} onSelectSubcategory={handleSelectSubcategory} primaryColor={primaryColor} bgClassName={isNatuCafe ? 'bg-[#0F0D0B]/95 border-[#2A2420]' : isCongelados ? 'bg-[#FBF5FF]/95 border-[#E8DDF0]' : undefined} isDark={isNatuCafe} data-id="element-6493" />

                {/* Menu items — filtered by active subcategory */}
                <div className="mt-8" data-id="element-6494">
                  <AnimatePresence mode="wait" data-id="element-6495">
                    {(() => {
                  const activeCategory = activeParentData.subcategories.find(c => c.id === activeSubcategory);
                  if (!activeCategory) return null;
                  const rawFiltered = items.filter(item => item.category_id === activeCategory.id);
                  // Sort items by DB sort_order
                  const filteredItems = [...rawFiltered].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
                  if (filteredItems.length === 0) return null;
                  return <motion.div key={activeSubcategory} initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.15
                  }} data-id="element-6496">
                          <div className="flex items-center gap-3 mb-3 sm:mb-4" data-id="element-6497">
                            <div className="w-1 h-5 rounded-full" style={{
                        backgroundColor: primaryColor
                      }} data-id="element-6498" />
                            <h3 className={`text-base sm:text-lg font-bold ${titleColor} tracking-tight`} data-id="element-6499">
                              {translateCategory(activeCategory.name, lang)}
                            </h3>
                            <span className={`text-xs ${isNatuCafe ? 'text-[#C6A969]/50' : isCongelados ? 'text-[#7B2D8E]/50' : 'text-[#8B7355]/50'} font-medium`} data-id="element-6500">
                              {filteredItems.length}
                            </span>
                          </div>
                          {(() => {
                      const catLower = activeCategory.name.toLowerCase();
                      const heroImage = catLower.includes('papas fritas') ? 'https://cdn.magicpatterns.com/uploads/mj5j2u1gh6rbCkbBBhXjFy/WhatsApp_Image_2026-05-09_at_10.41.55.jpg' : catLower.includes('palomitas') ? 'https://cdn.magicpatterns.com/uploads/mBshUmF5bcfUz6WWN4HARb/WhatsApp_Image_2026-05-09_at_10.18.21.jpg' : catLower.includes('tiritas') ? 'https://cdn.magicpatterns.com/uploads/aH1Q1EiLi85355G6T5T9BM/WhatsApp_Image_2026-05-09_at_10.38.25.jpg' : catLower.includes('nuggets') ? 'https://cdn.magicpatterns.com/uploads/tckbm39H3BbvzWPmVAEHWr/WhatsApp_Image_2026-05-09_at_10.40.07.jpg' : catLower.includes('alitas') ? 'https://cdn.magicpatterns.com/uploads/iVzbytMCpqtXqBGJW9ivLj/WhatsApp_Image_2026-05-09_at_10.43.26.jpg' : catLower.includes('mixto') ? 'https://cdn.magicpatterns.com/uploads/wtLKKB1rbNxnzH9eCL2o1E/WhatsApp_Image_2026-05-09_at_10.44.47.jpg' : catLower.includes('nacho pop') ? 'https://cdn.magicpatterns.com/uploads/hf6DC8o3D7VPQPdoyKz4QC/ChatGPT_Image_30_abr_2026,_08_14_12.png' : catLower.includes('refresco') ? 'https://cdn.magicpatterns.com/uploads/fGh9AmrJpgNfgvyY8FYDkR/ChatGPT_Image_30_abr_2026,_16_00_19.png' : null;
                      return heroImage ? <div className="flex flex-col md:flex-row gap-4 sm:gap-6" data-id="element-6501">
                                {/* Hero image */}
                                <div className="md:w-[30%] shrink-0" data-id="element-6502">
                                  <div className="sticky top-24 rounded-2xl overflow-hidden shadow-lg border border-[#E8DDD0]" data-id="element-6503">
                                    <img src={heroImage} alt={activeCategory.name} className="w-full aspect-square object-cover" loading="lazy" decoding="async" data-id="element-6504" />
                                    <div className="bg-gradient-to-t from-black/60 to-transparent absolute inset-0 rounded-2xl" data-id="element-6505" />
                                  </div>
                                </div>
                                {/* Product grid */}
                                <div className="flex-1 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4" data-id="element-6506">
                                  {filteredItems.map(item => {
                            const ti = translatedItems.find(t => t.id === item.id);
                            return <MenuItemCard key={item.id} item={item} onClick={() => handleProductClick(item)} primaryColor={primaryColor} displayName={ti?.name} displayDescription={ti?.description} theme={isKyomu ? 'kyomu' : isNatuCafe ? 'natucafe' : 'capricho'} data-id="element-6507" />;
                          })}
                                </div>
                              </div> : <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4" data-id="element-6508">
                                {filteredItems.map(item => {
                          const ti = translatedItems.find(t => t.id === item.id);
                          return <MenuItemCard key={item.id} item={item} onClick={() => handleProductClick(item)} primaryColor={primaryColor} displayName={ti?.name} displayDescription={ti?.description} theme={isKyomu ? 'kyomu' : isNatuCafe ? 'natucafe' : 'capricho'} data-id="element-6509" />;
                        })}
                              </div>;
                    })()}
                        </motion.div>;
                })()}
                  </AnimatePresence>
                </div>
              </>}

            {/* Fallback */}
            {categories.length === 0 && items.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4" data-id="element-6510">
                {items.map(item => {
              const ti = translatedItems.find(t => t.id === item.id);
              return <MenuItemCard key={item.id} item={item} onClick={() => handleProductClick(item)} primaryColor={primaryColor} displayName={ti?.name} displayDescription={ti?.description} theme={isKyomu ? 'kyomu' : isNatuCafe ? 'natucafe' : 'capricho'} data-id="element-6511" />;
            })}
              </div>}
          </div>
        </div>
      </main>

      <FloatingCartButton primaryColor={primaryColor} data-id="element-6512" />

      {selectedProduct && <ProductDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} item={selectedProduct} onAdd={handleAddToCart} primaryColor={primaryColor} data-id="element-6513" />}

      <Footer data-id="element-6514" />

      {/* Open Order Alert */}
      {showOpenOrderAlert && openOrder && <OpenOrderAlert order={openOrder} onDismiss={handleDismissOpenOrder} onViewTracking={handleCancelOpenOrder} data-id="element-6515" />}

      {/* Especialidades por Encargo Popup — Heladería Congelados */}
      {showEncargoPopup && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setShowEncargoPopup(false)} data-id="element-6516">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-6517" />
          <div className="relative bg-[#FBF5FF] rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden border border-[#E8DDF0]" onClick={e => e.stopPropagation()} data-id="element-6518">
            <div className="px-6 pt-8 pb-6 text-center" data-id="element-6519">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E8DDF0] flex items-center justify-center" data-id="element-6520">
                <span className="text-3xl" data-id="element-6521">🎂</span>
              </div>
              <h3 className="text-xl font-bold text-[#7B2D8E] mb-2" data-id="element-6522">
                Especialidades por Encargo
              </h3>
              <p className="text-[#7B2D8E]/70 text-sm leading-relaxed" data-id="element-6523">
                Pedidos con{' '}
                <span className="font-bold text-[#7B2D8E]" data-id="element-6524">
                  1 día de anticipación
                </span>
              </p>
            </div>
            <div className="px-6 pb-6" data-id="element-6525">
              <button onClick={() => {
            setShowEncargoPopup(false);
            if (pendingEncargoKey) {
              setActiveParent(pendingEncargoKey);
              const parent = parentGroups.find(p => p.key === pendingEncargoKey);
              if (parent && parent.subcategories.length > 0) {
                setActiveSubcategory(parent.subcategories[0].id);
              }
              setPendingEncargoKey(null);
              scrollToMenu();
            }
          }} className="w-full py-3 rounded-xl bg-[#7B2D8E] hover:bg-[#6A2579] text-white font-bold text-sm transition-colors" data-id="element-6526">
                Entendido
              </button>
            </div>
          </div>
        </div>}
    </div>;
}